<template>
  <modal
    :name="modalName"
    width="94%"
    height="auto"
    :max-width="960"
    :max-height="900"
    :adaptive="true"
    :scrollable="true"
    :click-to-close="false"
    @opened="focusClose"
    @before-close="beforeClose"
  >
    <section ref="dialog" class="insurance-run" role="dialog" aria-modal="true"
      aria-labelledby="insurance-run-title" @keydown.tab="trapFocus" @keyup.esc.stop.prevent="close">
      <AppModalHeader ref="header" design="new" title="Preview or send insurance updates"
        eyebrow="Client communications" title-id="insurance-run-title" @close="close" />

      <div class="insurance-run__body">
        <p class="insurance-run__intro">Choose what to do. Each client gets at most one email and one WhatsApp message from this campaign, so a message that was already sent is never sent again.</p>
        <fieldset class="insurance-run__fieldset" :disabled="working">
          <legend>What do you want to do?</legend>
          <div class="insurance-run__modes">
            <AppFilters design="new" name="insurance-update-mode" :filters="modes" :value="mode" @change="selectMode" />
          </div>
        </fieldset>

        <div class="insurance-run__selection" :class="`insurance-run__selection--${selectedMode.tone}`">
          <AppStatusBadge design="new" compact :tone="selectedMode.tone" :label="selectedMode.label" />
          <div>
            <strong>{{ selectedMode.title }}</strong>
            <p>{{ selectedMode.description }}</p>
          </div>
        </div>

        <div v-if="error" class="insurance-run__error" role="alert">{{ error }}</div>
        <section v-if="result" class="insurance-run__result" aria-live="polite">
          <div class="insurance-run__result-heading">
            <strong>{{ selectedMode.label }} completed</strong>
            <AppStatusBadge design="new" compact tone="green" label="Completed" />
          </div>
          <dl v-if="result.scan" class="insurance-run__metrics">
            <div><dt>Clients checked</dt><dd>{{ result.scan.clients }}</dd></div>
            <div><dt>Messages that can be sent</dt><dd>{{ result.scan.ready }}</dd></div>
            <div><dt>Messages that cannot be sent</dt><dd>{{ result.scan.blocked }}</dd></div>
            <div v-if="mode !== 'dry-run'"><dt>Records saved</dt><dd>{{ result.scan.created }}</dd></div>
          </dl>
          <dl v-if="result.dispatch" class="insurance-run__metrics">
            <div><dt>Picked up for sending</dt><dd>{{ result.dispatch.claimed }}</dd></div>
            <div><dt>Emails queued</dt><dd>{{ result.dispatch.queued }}</dd></div>
            <div><dt>WhatsApp messages sent</dt><dd>{{ result.dispatch.accepted }}</dd></div>
            <div><dt>Failed or not confirmed</dt><dd>{{ result.dispatch.failed + result.dispatch.unknown }}</dd></div>
          </dl>
          <p v-if="result.stale_claims" class="insurance-run__note">
            {{ result.stale_claims }} sends had no result after an hour and are now marked “Needs checking”
          </p>
          <div v-if="result.template_sync" class="insurance-run__template-sync">
            <strong>Template check</strong>
            <p v-if="result.template_sync.email && result.template_sync.email.registered !== undefined">{{ result.template_sync.email.registered }} of {{ result.template_sync.email.found }} email templates are ready · {{ result.template_sync.whatsapp && result.template_sync.whatsapp.found !== undefined ? result.template_sync.whatsapp.found : 0 }} WhatsApp templates found.</p>
            <p v-if="result.template_sync.email && result.template_sync.email.error">Could not check the email templates: {{ result.template_sync.email.error }}</p>
            <p v-else-if="result.template_sync.email && result.template_sync.email.missing_subject && result.template_sync.email.missing_subject.length">
              {{ result.template_sync.email.missing_subject.length }} email templates have no subject. Add a subject in Board or in the email service before those emails can be prepared.
              <span v-if="result.template_sync.email.missing_subject.includes('insurance_en')">The English template is one of them.</span>
            </p>
            <p v-if="result.template_sync.whatsapp && result.template_sync.whatsapp.error">Could not check the WhatsApp templates: {{ result.template_sync.whatsapp.error }}</p>
            <p v-else-if="result.template_sync.whatsapp && result.template_sync.whatsapp.found === 0">No insurance WhatsApp templates were found for the insurance WhatsApp line.</p>
          </div>
          <p v-if="mode === 'dry-run'" class="insurance-run__note">Nothing was saved and no messages were sent.</p>
          <div class="insurance-run__record-heading">
            <strong>Clients in this run</strong>
            <span>{{ result.records_total || 0 }} clients</span>
          </div>
          <div v-if="result.records && result.records.length" class="insurance-run__table-wrap">
            <table class="insurance-run__table">
              <thead><tr><th>Client</th><th>Group</th><th>Arrival</th><th>Email</th><th>WhatsApp</th></tr></thead>
              <tbody>
                <tr v-for="record in result.records" :key="record.client_id">
                  <td :title="record.client_name">{{ record.client_name }}</td>
                  <td :title="record.group ? record.group.name : ''">{{ record.group ? record.group.name : '—' }}</td>
                  <td>{{ dateOnly(record.arrival_date) }}</td>
                  <td><span :title="reasonLabel(record.email.reason)"><AppStatusBadge design="new" compact :tone="statusTone(record.email.status)" :label="statusLabel(record.email.status)" /></span></td>
                  <td><span :title="reasonLabel(record.whatsapp.reason)"><AppStatusBadge design="new" compact :tone="statusTone(record.whatsapp.status)" :label="statusLabel(record.whatsapp.status)" /></span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="insurance-run__note">No clients matched.</p>
          <p v-if="result.records_total > result.records.length" class="insurance-run__note">Showing the first {{ result.records.length }} clients.</p>
        </section>
      </div>

      <footer class="insurance-run__footer">
        <span v-if="confirming && !result" class="insurance-run__confirm">This will send real messages to clients and cannot be undone. Continue?</span>
        <AppButton v-if="confirming && !result" design="new" theme="dark" :disabled="working" @click.native="confirming = false">Go back</AppButton>
        <AppButton v-else design="new" theme="dark" :disabled="working" @click.native="close">{{ result ? 'Close' : 'Cancel' }}</AppButton>
        <AppButton design="new" :theme="selectedMode.buttonTheme" :disabled="working || Boolean(result)" @click.native="submit">
          {{ working ? 'Working…' : (confirming ? 'Yes, send to clients' : selectedMode.action) }}
        </AppButton>
      </footer>
    </section>
  </modal>
</template>

<script>
import { postGroundOpsInsuranceUpdateRun } from '@/http'
import { dateOnly, reasonLabel, statusLabel, statusTone } from './model'

const MODES = [
  { value: 'dry-run', label: 'Preview', tone: 'green', buttonTheme: 'success', title: 'See who would be included', description: 'Shows which clients would get a message and which cannot. Nothing is saved and nothing is sent.', action: 'Show preview' },
  { value: 'create', label: 'Prepare', tone: 'blue', buttonTheme: 'info', title: 'Prepare the messages', description: 'Saves a record for each eligible client and checks the templates. Nothing is sent to clients.', action: 'Prepare messages' },
  { value: 'send', label: 'Send prepared', tone: 'amber', buttonTheme: 'warn', title: 'Send the prepared messages', description: 'Sends the messages that are already prepared. Clients will receive them.', action: 'Send to clients' },
  { value: 'full', label: 'Prepare and send', tone: 'red', buttonTheme: 'error', title: 'Prepare and send in one step', description: 'Prepares messages for eligible clients and sends them right away. Clients will receive them.', action: 'Prepare and send' }
]

export default {
  name: 'InsuranceUpdatesRunModal',
  data() {
    return {
      modalName: 'insurance-updates-run',
      mode: 'dry-run',
      confirming: false,
      modes: MODES,
      working: false,
      result: null,
      error: ''
    }
  },
  computed: {
    selectedMode() { return this.modes.find(item => item.value === this.mode) },
    sendsMessages() { return this.mode === 'send' || this.mode === 'full' }
  },
  mounted() { this.$modal.show(this.modalName) },
  methods: {
    dateOnly,
    reasonLabel,
    statusLabel,
    statusTone,
    focusClose() { this.$refs.header.focusClose() },
    selectMode(value) { this.mode = value; this.result = null; this.error = ''; this.confirming = false },
    submit() {
      if (this.sendsMessages && !this.confirming) { this.confirming = true; return }
      this.run()
    },
    close() { if (!this.working) this.$modal.hide(this.modalName) },
    beforeClose(event) {
      if (this.working) event.cancel()
      else this.$emit('close')
    },
    async run() {
      if (this.working || this.result || (this.sendsMessages && !this.confirming)) return
      this.working = true
      this.error = ''
      try {
        const response = await postGroundOpsInsuranceUpdateRun(this.mode)
        this.result = response.data
        this.$emit('completed', this.mode)
      } catch (error) {
        const details = error.response && error.response.status === 400 && error.response.data && error.response.data.details
        this.confirming = false
        this.error = typeof details === 'string' ? details : (this.sendsMessages ? 'We did not get a confirmed result, so some messages may have been sent. Check the clients before trying again.' : 'Could not complete this. Please try again.')
      } finally {
        this.working = false
      }
    },
    trapFocus(event) {
      const elements = Array.from(this.$refs.dialog.querySelectorAll('button:not([disabled]),input:not([disabled]),[tabindex="0"]'))
        .filter(element => element.getClientRects().length)
      if (!elements.length) return
      const first = elements[0]
      const last = elements[elements.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
  }
}
</script>

<style scoped lang="scss">
.insurance-run { color: $ui-text; background: $ui-surface; font-family: $ui-font-body; }
.insurance-run__body { display: grid; gap: $ui-space-20; padding: $ui-space-24; }
.insurance-run__intro, .insurance-run__selection p, .insurance-run__note { margin: 0; color: $ui-muted; line-height: 1.5; }
.insurance-run__fieldset { min-width: 0; margin: 0; padding: 0; border: 0; }
.insurance-run__fieldset legend { margin-bottom: $ui-space-8; color: $ui-ink; font-weight: 600; }
.insurance-run__modes { overflow-x: auto; }
.insurance-run__modes :deep(.filters) { width: max-content; }
.insurance-run__selection { display: flex; align-items: flex-start; gap: $ui-space-16; padding: $ui-space-16; border: 1px solid $ui-line; border-radius: $ui-control-radius; background: $ui-surface-soft; }
.insurance-run__selection--green { border-color: $ui-green; background: $ui-green-soft; }
.insurance-run__selection--blue { border-color: $ui-blue; background: $ui-blue-soft; }
.insurance-run__selection--amber { border-color: $ui-amber; background: $ui-amber-soft; }
.insurance-run__selection--red { border-color: $ui-red; background: $ui-red-soft; }
.insurance-run__selection strong { display: block; margin-bottom: $ui-space-4; color: $ui-ink; }
.insurance-run__confirm { flex: 1; color: $ui-red; line-height: 1.4; }
.insurance-run__error { padding: $ui-space-16; border-radius: $ui-control-radius; color: $ui-red; background: $ui-red-soft; }
.insurance-run__result { padding: $ui-space-20; border: 1px solid $ui-line; border-radius: $ui-control-radius; }
.insurance-run__result-heading { display: flex; justify-content: space-between; align-items: center; gap: $ui-space-12; color: $ui-ink; }
.insurance-run__metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: $ui-space-12; margin: $ui-space-16 0 0; }
.insurance-run__metrics div { padding: $ui-space-12; border-radius: $ui-control-radius; background: $ui-surface-soft; }
.insurance-run__metrics dt { color: $ui-muted; font-size: $ui-font-size-metadata; }
.insurance-run__metrics dd { margin: $ui-space-4 0 0; color: $ui-ink; font-family: $ui-font-heading; font-size: $ui-font-size-section-title; font-weight: 600; }
.insurance-run__note { margin-top: $ui-space-16; font-size: $ui-font-size-metadata; }
.insurance-run__template-sync { margin-top: $ui-space-16; padding: $ui-space-12; border: 1px solid $ui-amber; border-radius: $ui-control-radius; background: $ui-amber-soft; color: $ui-ink; font-size: $ui-font-size-metadata; }
.insurance-run__template-sync p { margin: $ui-space-8 0 0; line-height: 1.5; }
.insurance-run__record-heading { display: flex; justify-content: space-between; gap: $ui-space-12; margin-top: $ui-space-20; padding-top: $ui-space-16; border-top: 1px solid $ui-line; }
.insurance-run__record-heading span { color: $ui-muted; font-size: $ui-font-size-metadata; }
.insurance-run__table-wrap { max-height: 340px; margin-top: $ui-space-12; overflow: auto; border: 1px solid $ui-line; border-radius: $ui-control-radius; }
.insurance-run__table { width: 100%; min-width: 740px; border-collapse: collapse; table-layout: fixed; }
.insurance-run__table th, .insurance-run__table td { overflow: hidden; padding: $ui-space-12; border-bottom: 1px solid $ui-line; text-align: left; text-overflow: ellipsis; white-space: nowrap; }
.insurance-run__table th { position: sticky; top: 0; background: $ui-surface-soft; color: $ui-muted; font-size: $ui-font-size-metadata; }
.insurance-run__table th:nth-child(1) { width: 24%; }
.insurance-run__table th:nth-child(2) { width: 22%; }
.insurance-run__table th:nth-child(3) { width: 16%; }
.insurance-run__table th:nth-child(4), .insurance-run__table th:nth-child(5) { width: 19%; }
.insurance-run__footer { display: flex; align-items: center; justify-content: flex-end; gap: $ui-space-12; padding: $ui-space-16 $ui-space-24; border-top: 1px solid $ui-line; }
@media (max-width: 600px) {
  .insurance-run__body { padding: $ui-space-16; }
  .insurance-run__metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .insurance-run__footer { flex-wrap: wrap; padding: $ui-space-16; }
}
</style>
